import React from 'react';
import { Typography, Box, Card } from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ScienceIcon from '@mui/icons-material/Science';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const ImplementationSection = () => {
  return (
    <section id="implementation" className="whitepaper-section">
      <Typography variant="h2" className="section-title">
        Implementation Strategy
      </Typography>

      <Typography variant="body1" sx={{ fontSize: '1.15rem', lineHeight: 1.8, mb: 5, color: '#e0e7ef', textAlign: 'center', maxWidth: '900px', mx: 'auto' }}>
        Our phased approach minimizes risk while delivering quick wins. Most organizations achieve production deployment in <strong style={{ color: '#10b981' }}>12-16 weeks</strong>.
      </Typography>

      <Box sx={{ position: 'relative', maxWidth: '1100px', mx: 'auto' }}>
        {/* Vertical Timeline Line */}
        <Box sx={{
          position: 'absolute',
          left: { xs: '20px', md: '50%' },
          top: '0',
          bottom: '0',
          width: '3px',
          background: 'linear-gradient(180deg, #60a5fa 0%, #10b981 35%, #f59e0b 65%, #8b5cf6 100%)',
          transform: { xs: 'none', md: 'translateX(-50%)' },
          zIndex: 0,
          boxShadow: '0 0 20px rgba(96,165,250,0.5)'
        }} />

        {/* Phase 1 - Blue */}
        <Box sx={{ 
          position: 'relative', 
          mb: 6,
          display: 'flex',
          alignItems: 'center',
          flexDirection: { xs: 'row', md: 'row' },
          justifyContent: { xs: 'flex-start', md: 'flex-start' }
        }}>
          {/* Timeline Dot */}
          <Box sx={{
            position: { xs: 'relative', md: 'absolute' },
            left: { xs: '0', md: '50%' },
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
            border: '4px solid rgba(10,34,64,1)',
            boxShadow: '0 0 30px rgba(96,165,250,0.6), 0 4px 16px rgba(0,0,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transform: { xs: 'none', md: 'translateX(-50%)' },
            zIndex: 2,
            flexShrink: 0,
            mr: { xs: 3, md: 0 }
          }}>
            <AssignmentIcon sx={{ color: '#fff', fontSize: '1.6rem' }} />
          </Box>

          {/* Content Card */}
          <Card sx={{
            width: { xs: 'calc(100% - 80px)', md: 'calc(50% - 60px)' },
            ml: { xs: 0, md: 'calc(50% + 60px)' },
            background: 'linear-gradient(135deg, rgba(96,165,250,0.15) 0%, rgba(59,130,246,0.1) 100%)',
            border: '2px solid rgba(96,165,250,0.4)',
            borderRadius: '20px',
            p: 4,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: '0 16px 48px rgba(96,165,250,0.3)',
              borderColor: 'rgba(96,165,250,0.6)'
            }
          }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2.5, color: '#60a5fa', fontSize: '1.5rem', display: 'flex', alignItems: 'center', gap: 1 }}>
              Phase 1: Assessment & Design
              <Box component="span" sx={{ fontSize: '1rem', color: '#94a3b8', fontWeight: 500 }}>(2-4 weeks)</Box>
            </Typography>
            <Box sx={{ mb: 3 }}>
              {[
                'Current state analysis of security data flows',
                'Volume profiling and cost modeling',
                'Architecture design and roadmap',
                'Technology selection and sizing',
                'Business case development'
              ].map((item, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1.5, color: '#e0e7ef' }}>
                  <CheckCircleOutlineIcon sx={{ color: '#60a5fa', fontSize: '1.2rem', mr: 1.5, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: '1.05rem', lineHeight: 1.6 }}>{item}</Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{
              background: 'rgba(10,34,64,0.6)',
              borderLeft: '4px solid #60a5fa',
              borderRadius: '8px',
              p: 2.5,
              mt: 3
            }}>
              <Typography sx={{ color: '#60a5fa', fontWeight: 700, fontSize: '1.05rem', mb: 0.5 }}>
                Deliverable:
              </Typography>
              <Typography sx={{ color: '#e0e7ef', fontSize: '1rem' }}>
                Architecture blueprint with 3-year TCO analysis
              </Typography>
            </Box>
          </Card>
        </Box>

        {/* Phase 2 - Green */}
        <Box sx={{ 
          position: 'relative', 
          mb: 6,
          display: 'flex',
          alignItems: 'center',
          flexDirection: { xs: 'row', md: 'row-reverse' },
          justifyContent: { xs: 'flex-start', md: 'flex-end' }
        }}>
          {/* Timeline Dot */}
          <Box sx={{
            position: { xs: 'relative', md: 'absolute' },
            left: { xs: '0', md: '50%' },
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            border: '4px solid rgba(10,34,64,1)',
            boxShadow: '0 0 30px rgba(16,185,129,0.6), 0 4px 16px rgba(0,0,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transform: { xs: 'none', md: 'translateX(-50%)' },
            zIndex: 2,
            flexShrink: 0,
            mr: { xs: 3, md: 0 }
          }}>
            <ScienceIcon sx={{ color: '#fff', fontSize: '1.6rem' }} />
          </Box>

          {/* Content Card */}
          <Card sx={{
            width: { xs: 'calc(100% - 80px)', md: 'calc(50% - 60px)' },
            mr: { xs: 0, md: 'calc(50% + 60px)' },
            background: 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(5,150,105,0.1) 100%)',
            border: '2px solid rgba(16,185,129,0.4)',
            borderRadius: '20px',
            p: 4,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: '0 16px 48px rgba(16,185,129,0.3)',
              borderColor: 'rgba(16,185,129,0.6)'
            }
          }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2.5, color: '#10b981', fontSize: '1.5rem', display: 'flex', alignItems: 'center', gap: 1 }}>
              Phase 2: Proof of Concept
              <Box component="span" sx={{ fontSize: '1rem', color: '#94a3b8', fontWeight: 500 }}>(4-6 weeks)</Box>
            </Typography>
            <Box sx={{ mb: 3 }}>
              {[
                'Deploy 3-node Confluent cluster',
                'Integrate 3-5 representative data sources',
                'Build filtering and enrichment pipelines',
                'Connect to existing SIEM for parallel validation',
                'Performance and cost validation'
              ].map((item, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1.5, color: '#e0e7ef' }}>
                  <CheckCircleOutlineIcon sx={{ color: '#10b981', fontSize: '1.2rem', mr: 1.5, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: '1.05rem', lineHeight: 1.6 }}>{item}</Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{
              background: 'rgba(10,34,64,0.6)',
              borderLeft: '4px solid #10b981',
              borderRadius: '8px',
              p: 2.5,
              mt: 3
            }}>
              <Typography sx={{ color: '#10b981', fontWeight: 700, fontSize: '1.05rem', mb: 0.5 }}>
                Success Criteria:
              </Typography>
              <Typography sx={{ color: '#e0e7ef', fontSize: '1rem' }}>
                Process 100K+ events/sec with 50%+ cost reduction
              </Typography>
            </Box>
          </Card>
        </Box>

        {/* Phase 3 - Orange */}
        <Box sx={{ 
          position: 'relative', 
          mb: 6,
          display: 'flex',
          alignItems: 'center',
          flexDirection: { xs: 'row', md: 'row' },
          justifyContent: { xs: 'flex-start', md: 'flex-start' }
        }}>
          {/* Timeline Dot */}
          <Box sx={{
            position: { xs: 'relative', md: 'absolute' },
            left: { xs: '0', md: '50%' },
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            border: '4px solid rgba(10,34,64,1)',
            boxShadow: '0 0 30px rgba(245,158,11,0.6), 0 4px 16px rgba(0,0,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transform: { xs: 'none', md: 'translateX(-50%)' },
            zIndex: 2,
            flexShrink: 0,
            mr: { xs: 3, md: 0 }
          }}>
            <RocketLaunchIcon sx={{ color: '#fff', fontSize: '1.6rem' }} />
          </Box>

          {/* Content Card */}
          <Card sx={{
            width: { xs: 'calc(100% - 80px)', md: 'calc(50% - 60px)' },
            ml: { xs: 0, md: 'calc(50% + 60px)' },
            background: 'linear-gradient(135deg, rgba(245,158,11,0.15) 0%, rgba(217,119,6,0.1) 100%)',
            border: '2px solid rgba(245,158,11,0.4)',
            borderRadius: '20px',
            p: 4,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: '0 16px 48px rgba(245,158,11,0.3)',
              borderColor: 'rgba(245,158,11,0.6)'
            }
          }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2.5, color: '#f59e0b', fontSize: '1.5rem', display: 'flex', alignItems: 'center', gap: 1 }}>
              Phase 3: Pilot Deployment
              <Box component="span" sx={{ fontSize: '1rem', color: '#94a3b8', fontWeight: 500 }}>(8-10 weeks)</Box>
            </Typography>
            <Box sx={{ mb: 3 }}>
              {[
                'Production-grade cluster deployment',
                'Integrate 20-30 data sources',
                'Implement all filtering/normalization rules',
                'Deploy monitoring and alerting',
                'Train SOC team on new workflows',
                'Run parallel for 30 days'
              ].map((item, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1.5, color: '#e0e7ef' }}>
                  <CheckCircleOutlineIcon sx={{ color: '#f59e0b', fontSize: '1.2rem', mr: 1.5, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: '1.05rem', lineHeight: 1.6 }}>{item}</Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{
              background: 'rgba(10,34,64,0.6)',
              borderLeft: '4px solid #f59e0b',
              borderRadius: '8px',
              p: 2.5,
              mt: 3
            }}>
              <Typography sx={{ color: '#f59e0b', fontWeight: 700, fontSize: '1.05rem', mb: 0.5 }}>
                Success Criteria:
              </Typography>
              <Typography sx={{ color: '#e0e7ef', fontSize: '1rem' }}>
                Zero data loss, 99.99% uptime, validated cost savings
              </Typography>
            </Box>
          </Card>
        </Box>

        {/* Phase 4 - Purple */}
        <Box sx={{ 
          position: 'relative', 
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          flexDirection: { xs: 'row', md: 'row-reverse' },
          justifyContent: { xs: 'flex-start', md: 'flex-end' }
        }}>
          {/* Timeline Dot */}
          <Box sx={{
            position: { xs: 'relative', md: 'absolute' },
            left: { xs: '0', md: '50%' },
            width: '50px',
            height: '50px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
            border: '4px solid rgba(10,34,64,1)',
            boxShadow: '0 0 30px rgba(139,92,246,0.6), 0 4px 16px rgba(0,0,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transform: { xs: 'none', md: 'translateX(-50%)' },
            zIndex: 2,
            flexShrink: 0,
            mr: { xs: 3, md: 0 }
          }}>
            <AutorenewIcon sx={{ color: '#fff', fontSize: '1.6rem' }} />
          </Box>

          {/* Content Card */}
          <Card sx={{
            width: { xs: 'calc(100% - 80px)', md: 'calc(50% - 60px)' },
            mr: { xs: 0, md: 'calc(50% + 60px)' },
            background: 'linear-gradient(135deg, rgba(139,92,246,0.15) 0%, rgba(124,58,237,0.1) 100%)',
            border: '2px solid rgba(139,92,246,0.4)',
            borderRadius: '20px',
            p: 4,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: '0 16px 48px rgba(139,92,246,0.3)',
              borderColor: 'rgba(139,92,246,0.6)'
            }
          }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2.5, color: '#8b5cf6', fontSize: '1.5rem', display: 'flex', alignItems: 'center', gap: 1 }}>
              Phase 4: Production Rollout
              <Box component="span" sx={{ fontSize: '1rem', color: '#94a3b8', fontWeight: 500 }}>(Ongoing)</Box>
            </Typography>
            <Box sx={{ mb: 3 }}>
              {[
                'Migrate remaining data sources',
                'Optimize filtering rules based on findings',
                'Implement advanced use cases',
                'Scale infrastructure as needed',
                'Continuous improvement and tuning'
              ].map((item, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1.5, color: '#e0e7ef' }}>
                  <CheckCircleOutlineIcon sx={{ color: '#8b5cf6', fontSize: '1.2rem', mr: 1.5, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: '1.05rem', lineHeight: 1.6 }}>{item}</Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{
              background: 'rgba(10,34,64,0.6)',
              borderLeft: '4px solid #8b5cf6',
              borderRadius: '8px',
              p: 2.5,
              mt: 3
            }}>
              <Typography sx={{ color: '#8b5cf6', fontWeight: 700, fontSize: '1.05rem', mb: 0.5 }}>
                Outcome:
              </Typography>
              <Typography sx={{ color: '#e0e7ef', fontSize: '1rem' }}>
                Full production deployment with ongoing optimization
              </Typography>
            </Box>
          </Card>
        </Box>
      </Box>
    </section>
  );
};

export default ImplementationSection;
